// User-facing graphics settings. Persisted to localStorage and applied to
// CONFIG.render at startup (and again whenever the settings modal toggles a
// value). Settings on the home screen don't have a live Renderer to mutate —
// the user sees the changes when they enter the next match. The Renderer
// constructor reads CONFIG.render fields, so writing them here is sufficient.

import { CONFIG } from '../config';

export type RenderScale = 0.1 | 0.25 | 0.5 | 0.75 | 1.0;
export type ShadowQuality = 'off' | 'low' | 'medium' | 'high';

export interface GraphicsSettings {
  renderScale: RenderScale;
  antiAliasing: boolean;
  cinematicLighting: boolean;
  shadows: ShadowQuality;
  bloom: boolean;
  cinematicFlares: boolean;
  motionBlur: boolean;
  cinematicColor: boolean;
  // Screen-Space Ambient Occlusion. Off by default.
  ambientOcclusion: boolean;
}

// Defaults match the Medium preset (see GRAPHICS_PRESETS.medium below). Only
// applied for users with no saved settings — existing users keep whatever
// they previously chose.
export const DEFAULTS: GraphicsSettings = {
  renderScale: 0.75,
  antiAliasing: false,
  cinematicLighting: true,
  shadows: 'low',
  bloom: true,
  cinematicFlares: true,
  motionBlur: true,
  cinematicColor: true,
  ambientOcclusion: false
};

const STORAGE_KEY = 'games:graphicsSettings';
const SHADOW_SIZES: Record<ShadowQuality, number> = {
  off: 0, low: 1024, medium: 2048, high: 4096
};
const RENDER_SCALES: ReadonlySet<RenderScale> = new Set<RenderScale>([0.1, 0.25, 0.5, 0.75, 1.0]);
const SHADOW_QUALITIES: ReadonlySet<ShadowQuality> = new Set<ShadowQuality>(['off', 'low', 'medium', 'high']);

export function loadGraphicsSettings(): GraphicsSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULTS };
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    return {
      renderScale: validRenderScale(parsed.renderScale) ?? DEFAULTS.renderScale,
      antiAliasing: typeof parsed.antiAliasing === 'boolean' ? parsed.antiAliasing : DEFAULTS.antiAliasing,
      cinematicLighting: typeof parsed.cinematicLighting === 'boolean' ? parsed.cinematicLighting : DEFAULTS.cinematicLighting,
      shadows: validShadow(parsed.shadows) ?? DEFAULTS.shadows,
      bloom: typeof parsed.bloom === 'boolean' ? parsed.bloom : DEFAULTS.bloom,
      cinematicFlares: typeof parsed.cinematicFlares === 'boolean' ? parsed.cinematicFlares : DEFAULTS.cinematicFlares,
      motionBlur: typeof parsed.motionBlur === 'boolean' ? parsed.motionBlur : DEFAULTS.motionBlur,
      cinematicColor: typeof parsed.cinematicColor === 'boolean' ? parsed.cinematicColor : DEFAULTS.cinematicColor,
      ambientOcclusion: typeof parsed.ambientOcclusion === 'boolean' ? parsed.ambientOcclusion : DEFAULTS.ambientOcclusion
    };
  } catch {
    return { ...DEFAULTS };
  }
}

export function saveGraphicsSettings(s: GraphicsSettings): void {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(s)); } catch { /* quota / private mode — ignore */ }
}

/** Re-apply the user's Cinematic Lighting choice AFTER Renderer.init().
 *  Renderer.init merges the active map's bundle.ibl block into CONFIG.render —
 *  some maps hardcode `mode: 'hdr'`, which would silently win over a user
 *  toggle of HDR off. Call this after init to put the user back in charge.
 *  The renderer.applyIbl() call is cheap once the room PMREM is cached. */
export function applyUserIblAfterInit(renderer: { applyIbl: () => void }): void {
  const s = loadGraphicsSettings();
  CONFIG.render.iblMode = s.cinematicLighting ? 'hdr' : 'off';
  renderer.applyIbl();
}

function validRenderScale(v: unknown): RenderScale | null {
  return RENDER_SCALES.has(v as RenderScale) ? (v as RenderScale) : null;
}
function validShadow(v: unknown): ShadowQuality | null {
  return SHADOW_QUALITIES.has(v as ShadowQuality) ? (v as ShadowQuality) : null;
}

/** Mutate CONFIG.render to match these settings. Safe to call before any
 *  Renderer exists — the constructor reads CONFIG.render at build time.
 *  When a match Renderer IS live (Renderer.active is set), also push the
 *  changes through `applyGraphicsLive()` so most settings take effect
 *  immediately (renderScale, IBL, post-pass options); shadows on/off
 *  alone still requires a match restart, see Renderer.applyGraphicsLive. */
export function applyGraphicsSettingsToConfig(s: GraphicsSettings): void {
  const r = CONFIG.render;
  r.renderScale = s.renderScale;
  r.aaMode = s.antiAliasing ? 'traa' : 'off';
  // Cinematic Lighting on → request HDR. Off → 'off' mode (scene.environment
  // null), which fully skips IBL sampling on every material rather than just
  // swapping the env cubemap for a cheaper one.
  r.iblMode = s.cinematicLighting ? 'hdr' : 'off';
  r.shadowsEnabled = s.shadows !== 'off';
  r.shadowMapSize = SHADOW_SIZES[s.shadows];
  r.bloom = s.bloom;
  r.anamorphic = s.cinematicFlares;
  r.motionBlur = s.motionBlur;
  r.lut = s.cinematicColor;
  r.ssao = s.ambientOcclusion;

  // Forward-declared late binding so this module doesn't import Renderer
  // (which would pull three.js into the home-screen bundle path
  // unnecessarily). Resolved lazily on first call after the match is up.
  Renderer_active?.applyGraphicsLive();
}

/** Lazy hook back to the match Renderer. Set by setLiveRendererHook so
 *  this file doesn't import Renderer.ts at all. */
let Renderer_active: { applyGraphicsLive(): void } | null = null;
export function setLiveRendererHook(r: { applyGraphicsLive(): void } | null): void {
  Renderer_active = r;
}

// ---------------------------------------------------------------------------
// Presets — five named buckets the user picks from in the Graphics tab.
// Each preset writes a full GraphicsSettings, so picking one sets every
// row at once. Names are i18n-keyed so translators can localise the
// preset labels alongside the joke quips.

export type GraphicsPresetId = 'ultra' | 'high' | 'medium' | 'low' | 'ultraLow';

export const GRAPHICS_PRESETS: Record<GraphicsPresetId, GraphicsSettings> = {
  ultra: {
    renderScale: 1.0,
    antiAliasing: true,
    cinematicLighting: true,
    shadows: 'high',
    bloom: true,
    cinematicFlares: true,
    motionBlur: true,
    cinematicColor: true,
    ambientOcclusion: true
  },
  high: {
    renderScale: 1.0,
    antiAliasing: true,
    cinematicLighting: true,
    shadows: 'medium',
    bloom: true,
    cinematicFlares: true,
    motionBlur: true,
    cinematicColor: true,
    ambientOcclusion: false
  },
  medium: {
    renderScale: 0.75,
    antiAliasing: false,
    cinematicLighting: true,
    shadows: 'low',
    bloom: true,
    cinematicFlares: true,
    motionBlur: true,
    cinematicColor: true,
    ambientOcclusion: false
  },
  low: {
    renderScale: 0.5,
    antiAliasing: false,
    cinematicLighting: true,
    shadows: 'off',
    bloom: false,
    cinematicFlares: false,
    motionBlur: false,
    cinematicColor: false,
    ambientOcclusion: false
  },
  ultraLow: {
    renderScale: 0.25,
    antiAliasing: false,
    cinematicLighting: false,
    shadows: 'off',
    bloom: false,
    cinematicFlares: false,
    motionBlur: false,
    cinematicColor: false,
    ambientOcclusion: false
  }
};

/** Display order used by the Graphics tab. Highest-fidelity first so
 *  the user reads the presets as a quality ladder descending. */
export const GRAPHICS_PRESET_ORDER: GraphicsPresetId[] = [
  'ultra', 'high', 'medium', 'low', 'ultraLow'
];

/** Match a GraphicsSettings against the known presets. Returns the
 *  matching id, or null when the user has hand-tweaked individual rows
 *  away from any named bucket. Used by the Graphics tab to highlight
 *  the active preset (or none) when the modal mounts. */
export function findActivePreset(s: GraphicsSettings): GraphicsPresetId | null {
  for (const id of GRAPHICS_PRESET_ORDER) {
    const p = GRAPHICS_PRESETS[id];
    if (p.renderScale === s.renderScale
      && p.antiAliasing === s.antiAliasing
      && p.cinematicLighting === s.cinematicLighting
      && p.shadows === s.shadows
      && p.bloom === s.bloom
      && p.cinematicFlares === s.cinematicFlares
      && p.motionBlur === s.motionBlur
      && p.cinematicColor === s.cinematicColor
      && p.ambientOcclusion === s.ambientOcclusion) {
      return id;
    }
  }
  return null;
}
